/**
 * Multi-format DMS document viewer.
 *
 * Dispatches on document_type and falls back to a download button if no
 * in-browser renderer fits. Image, PDF, JSON, CSV, XLSX, and DOCX are all
 * rendered inline; GIF works through the standard <img> tag (browsers
 * animate natively). Anything else is offered as a download.
 *
 * The viewer fetches the presigned GET URL when given a versionId and then
 * loads the file (or just embeds it for PDF/image). Caller is responsible for
 * mounting/unmounting; the component does its own loading state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Download, FileText, Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import { getVersionViewURL, type DMSDocumentType, type DMSDocumentVersion } from "../../lib/dmsApi";

interface FileViewerProps {
  version: DMSDocumentVersion;
  documentType: DMSDocumentType;
  /** Full-bleed within the parent (the DMS page); when false used as embedded panel (Asset Details). */
  flush?: boolean;
}

interface Sheet {
  name: string;
  columns: string[];
  rows: (string | number | null)[][];
}

type ViewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "url-ready"; url: string }
  | { kind: "text-ready"; text: string }
  | { kind: "table-ready"; sheets: Sheet[] }
  | { kind: "html-ready"; html: string }
  | { kind: "error"; message: string };


export function FileViewer({ version, documentType, flush = false }: FileViewerProps) {
  const [state, setState] = useState<ViewState>({ kind: "idle" });
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset on every version change so we don't show the previous file's
    // payload while the new one is loading.
    setState({ kind: "loading" });
    setDownloadUrl(null);

    let cancelled = false;

    (async () => {
      try {
        const info = await getVersionViewURL(version.id);
        if (cancelled) return;
        if (!info.view_url) {
          // Dev mode: no real S3, surface a friendly hint rather than a broken viewer.
          setState({
            kind: "error",
            message:
              "Dev mode: presigned S3 URLs are not generated. Set AWS credentials to view files.",
          });
          return;
        }
        setDownloadUrl(info.view_url);

        if (documentType === "pdf" || documentType === "image") {
          setState({ kind: "url-ready", url: info.view_url });
          return;
        }

        // Everything else needs the body bytes — fetch the presigned GET.
        const resp = await fetch(info.view_url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        if (documentType === "json" || documentType === "xml" || documentType === "text") {
          const text = await resp.text();
          if (cancelled) return;
          if (documentType === "json") {
            // Pretty-print if it parses; otherwise show raw.
            try {
              setState({ kind: "text-ready", text: JSON.stringify(JSON.parse(text), null, 2) });
            } catch {
              setState({ kind: "text-ready", text });
            }
          } else {
            setState({ kind: "text-ready", text });
          }
          return;
        }

        if (documentType === "csv") {
          const text = await resp.text();
          if (cancelled) return;
          const { default: Papa } = await import("papaparse");
          const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true });
          const rows = parsed.data as string[][];
          if (!rows.length) {
            setState({ kind: "table-ready", sheets: [{ name: "Sheet1", columns: [], rows: [] }] });
            return;
          }
          const columns = rows[0];
          setState({
            kind: "table-ready",
            sheets: [{ name: "Sheet1", columns, rows: rows.slice(1) }],
          });
          return;
        }

        if (documentType === "xlsx") {
          const buf = await resp.arrayBuffer();
          if (cancelled) return;
          const XLSX = await import("xlsx");
          const workbook = XLSX.read(buf, { type: "array" });
          // Parse EVERY sheet in the workbook so the viewer can show all of
          // them via the tab bar. Previously only SheetNames[0] was read,
          // which silently dropped sheets 2..N for multi-sheet workbooks.
          const sheets: Sheet[] = workbook.SheetNames.map((name) => {
            const ws = workbook.Sheets[name];
            const aoa = XLSX.utils.sheet_to_json<(string | number | null)[]>(ws, {
              header: 1, defval: null,
            });
            if (!aoa.length) return { name, columns: [], rows: [] };
            const columns = (aoa[0] ?? []).map((c) => String(c ?? ""));
            return { name, columns, rows: aoa.slice(1) };
          });
          setState({ kind: "table-ready", sheets });
          return;
        }

        if (documentType === "docx") {
          const buf = await resp.arrayBuffer();
          if (cancelled) return;
          const mammoth = await import("mammoth");
          const result = await mammoth.convertToHtml({ arrayBuffer: buf });
          setState({ kind: "html-ready", html: result.value });
          return;
        }

        // Unknown / "other" type → download fallback.
        setState({
          kind: "error",
          message: "This file type can't be previewed in-browser. Use the download button.",
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ kind: "error", message: `Could not load file: ${message}` });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [version.id, documentType]);

  const containerClass = flush
    ? "h-full w-full bg-surface-900/40 flex flex-col"
    : "rounded-xl border border-white/10 bg-surface-900/40 flex flex-col min-h-[480px]";

  return (
    <div className={containerClass}>
      <ViewerHeader version={version} downloadUrl={downloadUrl} />
      <div className="flex-1 min-h-0 overflow-auto">
        <ViewerBody state={state} version={version} downloadUrl={downloadUrl} />
      </div>
    </div>
  );
}


function ViewerHeader({ version, downloadUrl }: {
  version: DMSDocumentVersion; downloadUrl: string | null;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-2.5 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-4 w-4 text-surface-400 shrink-0" />
        <span className="text-sm text-surface-100 truncate">{version.file_name}</span>
        <span className="text-xs text-surface-500 whitespace-nowrap">
          · v{version.version_number} · {formatBytes(version.file_size_bytes)}
        </span>
      </div>
      {downloadUrl && (
        <a
          href={downloadUrl}
          download={version.file_name}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-surface-200 hover:bg-white/10"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </a>
      )}
    </div>
  );
}


function ViewerBody({
  state, version, downloadUrl,
}: { state: ViewState; version: DMSDocumentVersion; downloadUrl: string | null }) {
  if (state.kind === "idle" || state.kind === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-surface-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm">Loading…</span>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-amber-400" />
        <p className="text-sm text-surface-300 max-w-md">{state.message}</p>
        {downloadUrl && (
          <a href={downloadUrl} download={version.file_name}>
            <Button size="sm" variant="secondary">
              <Download className="h-4 w-4" /> Download instead
            </Button>
          </a>
        )}
      </div>
    );
  }
  if (state.kind === "url-ready") {
    // PDFs / images
    if (state.url) {
      // Heuristic: if the version is an image, render an <img> so animated
      // GIFs animate. Otherwise let the browser handle the file (PDF in iframe).
      const isImage = version.content_type.startsWith("image/");
      if (isImage) {
        return (
          <div className="flex h-full items-center justify-center bg-black/30 p-4">
            <img
              src={state.url}
              alt={version.file_name}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        );
      }
      return (
        <iframe
          src={state.url}
          title={version.file_name}
          className="h-full w-full"
          style={{ border: 0 }}
        />
      );
    }
  }
  if (state.kind === "text-ready") {
    return (
      <pre className="m-0 whitespace-pre-wrap break-words p-4 text-xs font-mono text-surface-200">
        {state.text}
      </pre>
    );
  }
  if (state.kind === "table-ready") {
    return <SheetsView sheets={state.sheets} />;
  }
  if (state.kind === "html-ready") {
    return (
      <div
        className="prose prose-invert max-w-none p-6 text-surface-200"
        // Mammoth output is HTML derived from a .docx the same user uploaded,
        // not from arbitrary internet input. Acceptable XSS surface for an
        // authenticated DMS — but worth revisiting if the upload set widens.
        dangerouslySetInnerHTML={{ __html: state.html }}
      />
    );
  }
  return null;
}


/**
 * Renders one or many sheets from a parsed workbook. A tab bar is only
 * shown when the workbook has 2+ sheets; CSVs and single-sheet XLSX files
 * keep the previous, tab-free look. Sheet selection lives in this
 * component's local state so it survives parent re-renders that don't
 * touch the version itself.
 */
function SheetsView({ sheets }: { sheets: Sheet[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  // Clamp the index whenever the workbook itself changes — defensive
  // guard for the (currently impossible) case where a new file has fewer
  // sheets than the previously selected index.
  const safeIndex = activeIndex < sheets.length ? activeIndex : 0;
  const active = sheets[safeIndex];

  if (!sheets.length) {
    return <p className="p-4 text-sm text-surface-400">Empty spreadsheet.</p>;
  }

  return (
    <div className="flex h-full flex-col">
      {sheets.length > 1 && (
        <div
          className="flex flex-shrink-0 items-center gap-1 overflow-x-auto border-b border-white/10 bg-surface-900/60 px-2 py-1.5"
          role="tablist"
          aria-label="Workbook sheets"
        >
          {sheets.map((s, i) => {
            const isActive = i === safeIndex;
            return (
              <button
                key={`${s.name}-${i}`}
                onClick={() => setActiveIndex(i)}
                role="tab"
                aria-selected={isActive}
                className={
                  "shrink-0 rounded-md px-3 py-1 text-xs font-medium transition-colors " +
                  (isActive
                    ? "bg-brand-500/20 text-brand-200"
                    : "text-surface-400 hover:bg-white/[0.06] hover:text-surface-200")
                }
              >
                {s.name || `Sheet ${i + 1}`}
              </button>
            );
          })}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {active.columns.length === 0 ? (
          <p className="p-4 text-sm text-surface-400">This sheet is empty.</p>
        ) : (
          <table className="min-w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-surface-900/95 backdrop-blur">
              <tr>
                {active.columns.map((c, i) => (
                  <th
                    key={i}
                    className="border-b border-white/10 px-3 py-2 text-left font-semibold text-surface-200"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {active.rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-white/[0.03]">
                  {active.columns.map((_, ci) => (
                    <td
                      key={ci}
                      className="border-b border-white/[0.04] px-3 py-1.5 text-surface-300"
                    >
                      {formatCell(row[ci])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ── helpers ────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatCell(v: string | number | null): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return String(v);
  return String(v);
}

// Allow the parent to reason about a document's renderable type without
// re-importing the enum.
export const RENDERABLE_TYPES: DMSDocumentType[] = [
  "pdf", "image", "csv", "xlsx", "json", "xml", "text", "docx",
];

export function useFileViewerStub() {
  // Placeholder export to silence accidental unused-export lint warnings
  // in future tree-shake passes. No-op.
  return useMemo(() => null, []);
}
